import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  Avatar,
  Stack,
  Divider,
  InputAdornment,
  Menu,
  MenuItem as MenuItemComponent,
  ListItemIcon,
  ListItemText,
  Badge,
  useTheme,
  useMediaQuery,
  Fab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText as MuiListItemText,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add,
  CloudUpload,
  Search,
  Delete,
  Edit,
  ExpandMore,
  Folder,
  Description,
  Analytics,
  CheckCircle,
  Error,
  HourglassEmpty,
  Download,
  Visibility,
  FileDownload,
  Category,
  Storage,
  QueryStats,
  AutoAwesome,
  SmartToy,
  TrendingUp
} from '@mui/icons-material';
import api from '../services/api';

// Types
interface Document {
  id: number;
  title: string;
  file_name: string;
  file_type: string;
  file_size: number;
  status: 'processing' | 'indexed' | 'failed';
  error_message?: string;
  created_at: string;
  indexed_at?: string;
}

interface Category {
  id: number;
  name: string;
  description?: string;
  color: string;
  icon: string;
  created_at: string;
}

interface SearchResult {
  chunk_id: number;
  content: string;
  document_title: string;
  document_id: number;
  file_name: string;
  similarity_score: number;
}

interface KBAnalytics {
  document_stats: Record<string, number>;
  total_chunks: number;
  total_size_bytes: number;
  total_size_mb: number;
}

const KnowledgeBasePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [analytics, setAnalytics] = useState<KBAnalytics | null>(null);
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [editCategoryDialogOpen, setEditCategoryDialogOpen] = useState(false);

  // Form states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryDescription, setEditCategoryDescription] = useState('');

  // Load data
  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/knowledge-base/documents');
      setDocuments(response.data.documents);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load documents', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const response = await api.get('/knowledge-base/categories');
      setCategories(response.data.categories);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load categories', severity: 'error' });
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const response = await api.get('/knowledge-base/analytics');
      setAnalytics(response.data);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load analytics', severity: 'error' });
    }
  }, []);

  useEffect(() => {
    loadDocuments();
    loadCategories();
    loadAnalytics();
  }, [loadDocuments, loadCategories, loadAnalytics]);

  // Handlers
  const handleFileUpload = async () => {
    if (!selectedFile || !uploadTitle) {
      setSnackbar({ open: true, message: 'Please select a file and enter a title', severity: 'error' });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadTitle);

      if (selectedCategories.length > 0) {
        formData.append('category_ids', JSON.stringify(selectedCategories));
      }

      await api.post('/knowledge-base/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSnackbar({ open: true, message: 'Document uploaded successfully', severity: 'success' });
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setUploadTitle('');
      setSelectedCategories([]);
      loadDocuments();
      loadAnalytics();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to upload document', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName) {
      setSnackbar({ open: true, message: 'Category name is required', severity: 'error' });
      return;
    }

    try {
      await api.post('/knowledge-base/categories', {
        name: newCategoryName,
        description: newCategoryDescription,
        color: '#3498db',
        icon: 'folder'
      });

      setSnackbar({ open: true, message: 'Category created successfully', severity: 'success' });
      setCategoryDialogOpen(false);
      setNewCategoryName('');
      setNewCategoryDescription('');
      loadCategories();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to create category', severity: 'error' });
    }
  };

  const handleSearch = async () => {
    if (!searchQuery) {
      setSnackbar({ open: true, message: 'Please enter a search query', severity: 'error' });
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/knowledge-base/search', {
        query: searchQuery,
        limit: 10,
        similarity_threshold: 0.7
      });

      setSearchResults(response.data.results);
      setSearchDialogOpen(true);
    } catch (error) {
      setSnackbar({ open: true, message: 'Search failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await api.delete(`/knowledge-base/documents/${documentId}`);
      setSnackbar({ open: true, message: 'Document deleted successfully', severity: 'success' });
      loadDocuments();
      loadAnalytics();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete document', severity: 'error' });
    }
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setEditCategoryName(category.name);
    setEditCategoryDescription(category.description || '');
    setEditCategoryDialogOpen(true);
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory || !editCategoryName) {
      setSnackbar({ open: true, message: 'Category name is required', severity: 'error' });
      return;
    }

    try {
      await api.put(`/knowledge-base/categories/${editingCategory.id}`, {
        name: editCategoryName,
        description: editCategoryDescription
      });

      setSnackbar({ open: true, message: 'Category updated successfully', severity: 'success' });
      setEditCategoryDialogOpen(false);
      setEditingCategory(null);
      setEditCategoryName('');
      setEditCategoryDescription('');
      loadCategories();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update category', severity: 'error' });
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (!window.confirm('Are you sure you want to delete this category? This will also delete all associated articles.')) {
      return;
    }

    try {
      await api.delete(`/knowledge-base/categories/${categoryId}`);
      setSnackbar({ open: true, message: 'Category deleted successfully', severity: 'success' });
      loadCategories();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete category', severity: 'error' });
    }
  };

  // Render helpers
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'indexed':
        return <CheckCircle color="success" />;
      case 'processing':
        return <HourglassEmpty color="warning" />;
      case 'failed':
        return <Error color="error" />;
      default:
        return <HourglassEmpty />;
    }
  };

  const getStatusChip = (status: string) => {
    const colors = {
      indexed: 'success' as const,
      processing: 'warning' as const,
      failed: 'error' as const
    };

    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={colors[status as keyof typeof colors] || 'default'}
        size="small"
      />
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Render components
  const renderDocumentsTab = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Documents ({documents.length})
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Search />}
            onClick={() => {
              setSearchQuery('');
              setSearchDialogOpen(true);
            }}
          >
            Search
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Document
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress />}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Description sx={{ mr: 1, color: 'text.secondary' }} />
                    {doc.title}
                  </Box>
                </TableCell>
                <TableCell>{doc.file_name}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    {getStatusIcon(doc.status)}
                    <Box ml={1}>
                      {getStatusChip(doc.status)}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                <TableCell>
                  {new Date(doc.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteDocument(doc.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderCategoriesTab = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Categories ({categories.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCategoryDialogOpen(true)}
        >
          Create Category
        </Button>
      </Box>

      <Grid container spacing={3}>
        {categories.map((category) => (
          <Grid item xs={12} sm={6} md={4} key={category.id}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center">
                    <Avatar
                      sx={{
                        bgcolor: category.color,
                        width: 40,
                        height: 40,
                        mr: 2
                      }}
                    >
                      <Folder />
                    </Avatar>
                    <Typography variant="h6">{category.name}</Typography>
                  </Box>
                  <Box>
                    <Tooltip title="Edit Category">
                      <IconButton
                        size="small"
                        onClick={() => handleEditCategory(category)}
                        color="primary"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Category">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCategory(category.id)}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {category.description && (
                  <Typography color="text.secondary" variant="body2">
                    {category.description}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderAnalyticsTab = () => (
    <Box>
      <Typography variant="h6" mb={3}>
        Knowledge Base Analytics
      </Typography>

      {analytics && (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Description sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {analytics.document_stats.indexed || 0}
                    </Typography>
                    <Typography color="text.secondary">
                      Indexed Documents
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Storage sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {analytics.total_chunks}
                    </Typography>
                    <Typography color="text.secondary">
                      Knowledge Chunks
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <QueryStats sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {analytics.total_size_mb}
                    </Typography>
                    <Typography color="text.secondary">
                      MB Stored
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingUp sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                  <Box>
                    <Typography variant="h4">
                      {analytics.document_stats.processing || 0}
                    </Typography>
                    <Typography color="text.secondary">
                      Processing
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );

  const renderSearchDialog = () => (
    <Dialog
      open={searchDialogOpen}
      onClose={() => setSearchDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Search Knowledge Base</DialogTitle>
      <DialogContent>
        <Box mb={3}>
          <TextField
            fullWidth
            label="Search Query"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} disabled={loading}>
                    <Search />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>

        {loading && <LinearProgress />}

        {searchResults.length > 0 && (
          <Box mt={3}>
            <Typography variant="h6" mb={2}>
              Search Results ({searchResults.length})
            </Typography>
            {searchResults.map((result, index) => (
              <Card key={`${result.chunk_id}-${index}`} sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" color="primary">
                      {result.document_title}
                    </Typography>
                    <Chip
                      label={`${(result.similarity_score * 100).toFixed(1)}% match`}
                      size="small"
                      color="primary"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {result.file_name}
                  </Typography>
                  <Typography variant="body2">
                    {result.content}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSearchDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Knowledge Base
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your Co-Pilot knowledge base with semantic search capabilities
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <SmartToy sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
          <AutoAwesome sx={{ fontSize: 24, color: 'secondary.main' }} />
        </Box>
      </Box>

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label="Documents"
            icon={<Description />}
            iconPosition="start"
          />
          <Tab
            label="Categories"
            icon={<Category />}
            iconPosition="start"
          />
          <Tab
            label="Analytics"
            icon={<Analytics />}
            iconPosition="start"
          />
        </Tabs>

        <Box p={3}>
          {activeTab === 0 && renderDocumentsTab()}
          {activeTab === 1 && renderCategoriesTab()}
          {activeTab === 2 && renderAnalyticsTab()}
        </Box>
      </Paper>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <TextField
              fullWidth
              label="Document Title"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              sx={{ mb: 3 }}
            />

            <input
              type="file"
              accept=".pdf,.docx,.txt,.md"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              style={{ marginBottom: 16, width: '100%' }}
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Categories</InputLabel>
              <Select
                multiple
                value={selectedCategories}
                onChange={(e) => setSelectedCategories(e.target.value as number[])}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const category = categories.find(c => c.id === value);
                      return (
                        <Chip
                          key={value}
                          label={category?.name}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert severity="info">
              Supported formats: PDF, DOCX, TXT, MD (max 10MB)
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleFileUpload}
            variant="contained"
            disabled={!selectedFile || !uploadTitle || loading}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Category</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <TextField
              fullWidth
              label="Category Name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={newCategoryDescription}
              onChange={(e) => setNewCategoryDescription(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateCategory}
            variant="contained"
            disabled={!newCategoryName}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog
        open={editCategoryDialogOpen}
        onClose={() => setEditCategoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <TextField
              fullWidth
              label="Category Name"
              value={editCategoryName}
              onChange={(e) => setEditCategoryName(e.target.value)}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={editCategoryDescription}
              onChange={(e) => setEditCategoryDescription(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCategoryDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateCategory}
            variant="contained"
            disabled={!editCategoryName}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Search Dialog */}
      {renderSearchDialog()}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KnowledgeBasePage;